import { api } from '@/api';
import { removeLocalToken } from '@/utils';
import router from '@/router';
import { ActionContext } from 'vuex';
import {
  commitSetToken,
  commitSetLoggedIn,
  commitSetLogInError,
  dispatchGetUserProfile,
  dispatchRouteLoggedIn,
  dispatchLogOut,
  commitSetUserProfile,
  dispatchCheckApiError,
  dispatchRemoveLogIn,
  dispatchRouteLogOut,
  commitRemoveNotification,
  commitAddNotification,
  commitSetNotifications,
  commitSetVersion,
  commitSetConfig,
} from './accessors';
import { AxiosError } from 'axios';
import { State } from '../state';
import { MainState, AppNotification } from './state';
import { IConfig } from '@/interfaces/config';

type MainContext = ActionContext<MainState, State>;

export const actions = {
  async actionLogIn(context: MainContext, payload: { username: string; password: string }) {
    try {
      commitSetLoggedIn(context, true);
      commitSetLogInError(context, false);
      await dispatchGetUserProfile(context);
      await dispatchRouteLoggedIn(context);
      commitAddNotification(context, { content: 'Logged in', color: 'success' });
    } catch (err) {
      commitSetLogInError(context, true);
      await dispatchLogOut(context);
    }
  },
  async actionGetUserProfile(context: MainContext) {
    try {
      const response = await api.getMe();
      if (response.data) {
        commitSetUserProfile(context, response.data);
      }
    } catch (error) {
      await dispatchCheckApiError(context, error);
    }
  },
  async actionUpdateUserProfile(context: MainContext, payload) {
    try {
      const loadingNotification = { content: 'saving', showProgress: true };
      commitAddNotification(context, loadingNotification);
      const response = (
        await Promise.all([
          api.updateMe(context.state.token, payload),
          await new Promise((resolve, reject) => setTimeout(() => resolve(), 500)),
        ])
      )[0];
      commitSetUserProfile(context, response.data);
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, { content: 'Profile successfully updated', color: 'success' });
    } catch (error) {
      await dispatchCheckApiError(context, error);
    }
  },
  async actionCheckLoggedIn(context: MainContext) {
    try {
      const response = await api.getMe();
      commitSetLoggedIn(context, true);
      commitSetUserProfile(context, response.data);
    } catch (error) {
      setTimeout(async () => {
        const response = await api.getMe();
        commitSetLoggedIn(context, true);
        commitSetUserProfile(context, response.data);
      }, 3000);
    }
  },
  async actionRemoveLogIn(context: MainContext) {
    removeLocalToken();
    commitSetToken(context, '');
    commitSetLoggedIn(context, false);
  },
  async actionLogOut(context: MainContext) {
    await dispatchRemoveLogIn(context);
    await dispatchRouteLogOut(context);
  },
  async actionUserLogOut(context: MainContext) {
    await dispatchLogOut(context);
    commitAddNotification(context, { content: 'Logged out', color: 'success' });
  },
  actionRouteLogOut(context: MainContext) {
    if (router.currentRoute.path !== '/login') {
      router.push('/login');
    }
  },
  async actionCheckApiError(context: MainContext, payload: AxiosError) {
    if (payload.response!.status === 401) {
      await dispatchLogOut(context);
    } else if (payload?.response?.data?.hasOwnProperty('detail')) {
      commitSetNotifications(context, [{ content: payload.response!.data.detail, color: 'error' }]);
    } else {
      commitSetNotifications(context, [{ content: 'An error occurred while processing your request', color: 'error' }]);
    }
  },
  actionRouteLoggedIn(context: MainContext) {
    if (router.currentRoute.path === '/') {
      router.push('/main');
    }
  },
  async removeNotification(context: MainContext, payload: { notification: AppNotification; timeout: number }) {
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        commitRemoveNotification(context, payload.notification);
        resolve(true);
      }, payload.timeout);
    });
  },
  async actionGetVersion(context: MainContext) {
    try {
      const response = await api.getVersion();
      if (response.data) commitSetVersion(context, response.data);
    } catch (error) {
      await dispatchCheckApiError(context, error);
      throw error;
    }
  },
  async actionGetConfig(context: MainContext) {
    try {
      const response = await api.getConfig();
      const payload: IConfig = {
        messagesStatsUrl: response.data.MESSAGES_STATS_URL || '',
        messagesApiUrl: response.data.MESSAGES_API_URL || '',
        authApiUrl: response.data.AUTH_API_URL || '',
      };
      commitSetConfig(context, payload);
    } catch (error) {
      await dispatchCheckApiError(context, error);
      throw error;
    }
  },
};
